import { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation, Outlet } from 'react-router-dom';
import {
  LayoutDashboard, Package, Users, ShoppingCart, Tag, Gift,
  BarChart3, Settings, LogOut, Menu, X, ChevronRight, Layers, Globe, User
} from 'lucide-react';
import { useAuth } from '../../context/AuthContext';

export default function AdminLayout() {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout, loading } = useAuth();
  const [sidebarOpen, setSidebarOpen] = useState(false);

  useEffect(() => {
    if (!loading && (!user || user.role !== 'admin')) {
      navigate('/login');
    }
  }, [user, navigate, loading]);

  if (loading) {
    return (
      <div className="min-h-screen bg-surface flex items-center justify-center">
        <div className="w-16 h-16 border-4 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!user || user.role !== 'admin') return null;

  const handleLogout = async () => {
    await logout();
    navigate('/');
  };

  const menuItems = [
    { icon: LayoutDashboard, label: 'Dashboard', path: '/admin' },
    { icon: Package, label: 'Products', path: '/admin/products' },
    { icon: Layers, label: 'Categories', path: '/admin/categories' },
    { icon: ShoppingCart, label: 'Orders', path: '/admin/orders' },
    { icon: Users, label: 'Users', path: '/admin/users' },
    { icon: Tag, label: 'Coupons', path: '/admin/coupons' },
    { icon: BarChart3, label: 'Reports', path: '/admin/reports' },
    { icon: User, label: 'Profile', path: '/admin/profile' },
  ];

  const isActive = (path) => {
    if (path === '/admin') {
      return location.pathname === '/admin';
    }
    return location.pathname.startsWith(path);
  };

  return (
    <div className="min-h-screen bg-surface" data-testid="admin-layout">
      {/* Mobile Header */}
      <div className="lg:hidden sticky top-0 z-50 bg-white border-b border-gray-100 px-4 py-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <button onClick={() => setSidebarOpen(true)} className="p-2 hover:bg-surface rounded-lg">
              <Menu className="w-5 h-5" />
            </button>
            <Link to="/admin" className="flex items-center space-x-0">
              <img
                src="/logo.png"
                alt="Nso-India"
                className="h-6 w-auto object-contain"
              />
              <span className="text-xs bg-primary/10 text-primary px-2 py-0.5 rounded-full ml-2">Admin</span>
            </Link>
          </div>
        </div>
      </div>

      <div className="flex">
        {/* Sidebar */}
        <aside className={`
          fixed top-0 left-0 h-screen w-64 bg-white border-r border-gray-100 z-50
          transform transition-transform duration-300 lg:transform-none flex flex-col
          ${sidebarOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}
        `}>
          {/* Sidebar Header */}
          <div className="p-6 border-b border-gray-100 flex-shrink-0">
            <div className="flex items-center justify-between">
              <Link to="/admin" className="flex items-center space-x-0">
                <img
                  src="/logo.png"
                  alt="Nso-India"
                  className="h-7 w-auto object-contain"
                />
              </Link>
              <button
                onClick={() => setSidebarOpen(false)}
                className="lg:hidden p-2 hover:bg-surface rounded-lg"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            <span className="text-xs bg-primary/10 text-primary px-2 py-0.5 rounded-full mt-2 inline-block">
              Admin Panel
            </span>
          </div>

          {/* Navigation - Internally Scrollable */}
          <nav className="flex-1 overflow-y-auto p-4 space-y-1 custom-scrollbar">
            {menuItems.map((item) => (
              <Link
                key={item.path}
                to={item.path}
                onClick={() => setSidebarOpen(false)}
                className={`flex items-center justify-between px-4 py-3 rounded-xl transition-all font-body ${isActive(item.path)
                  ? 'bg-primary text-white shadow-md'
                  : 'text-secondary hover:bg-surface'
                  }`}
                data-testid={`admin-menu-${item.label.toLowerCase()}`}
              >
                <div className="flex items-center gap-3">
                  <item.icon className="w-5 h-5" />
                  <span>{item.label}</span>
                </div>
                <ChevronRight className={`w-4 h-4 ${isActive(item.path) ? 'opacity-100' : 'opacity-40'}`} />
              </Link>
            ))}
          </nav>

          {/* User Info & Logout - Fixed at bottom */}
          <div className="p-4 border-t border-gray-100 bg-white flex-shrink-0">
            <Link 
              to="/admin/profile" 
              onClick={() => setSidebarOpen(false)}
              className="flex items-center gap-3 mb-4 px-2 hover:bg-surface rounded-xl py-2 transition-all group"
            >
              <div className="w-10 h-10 bg-primary/10 rounded-full flex items-center justify-center group-hover:bg-primary group-hover:text-white transition-all">
                <span className="font-body font-semibold text-primary group-hover:text-white transition-all">
                  {user.name?.charAt(0).toUpperCase()}
                </span>
              </div>
              <div className="min-w-0">
                <p className="font-body font-medium text-secondary text-sm truncate group-hover:text-primary transition-all">{user.name}</p>
                <p className="text-xs text-muted-foreground truncate">{user.email}</p>
              </div>
            </Link>
            <div className="flex gap-2">
              <Link
                to="/"
                className="flex-1 px-3 py-2 text-center text-sm font-body text-muted-foreground hover:text-secondary hover:bg-surface rounded-lg transition-colors border border-transparent hover:border-gray-100"
              >
                Store
              </Link>
              <button
                onClick={handleLogout}
                className="flex items-center justify-center gap-2 px-3 py-2 text-sm font-body text-destructive hover:bg-destructive/10 rounded-lg transition-colors"
              >
                <LogOut className="w-4 h-4" />
                Logout
              </button>
            </div>
          </div>
        </aside>

        {/* Overlay */}
        {sidebarOpen && (
          <div
            className="fixed inset-0 bg-black/50 z-40 lg:hidden"
            onClick={() => setSidebarOpen(false)}
          />
        )}

        {/* Main Content */}
        <main className="flex-1 min-h-screen p-4 lg:p-8 lg:ml-64">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
